// @ts-nocheck
import PropTypes from "prop-types";
import {transformFiguresToAnimalData} from "../../../../../utils/lotteryDataTransformers";

const gridContainerCls =
  "w-full max-w-[800px] h-auto flex flex-col gap-1 max-[768px]:max-w-full max-[768px]:gap-0.5 max-[768px]:px-2 max-[420px]:px-1";
const topSectionCls =
  "w-full h-[var(--size-45)] grid grid-cols-2 gap-1 max-[768px]:h-[var(--size-30)] max-[768px]:gap-0.5 max-[420px]:h-[var(--size-25)]";
const specialButtonBaseCls =
  "flex-1 bg-[var(--color-roulette-green)] border border-[var(--color-secondary)] text-white cursor-pointer flex flex-col items-center justify-center transition-all duration-200 relative p-2.5 overflow-hidden hover:opacity-80 hover:scale-[1.02] max-[768px]:w-auto";
const special1Cls =
  "!bg-[radial-gradient(circle,var(--color-roulette-green-gradient)_0%,var(--color-roulette-green)_60%)] !rounded-none !rounded-tl-[20px] !border !border-[var(--color-secondary)]";
const special2Cls =
  "!bg-[radial-gradient(circle,var(--color-roulette-green-gradient)_0%,var(--color-roulette-green)_60%)] !rounded-none !rounded-tr-[20px] !border !border-[var(--color-secondary)]";
const greenCls = "bg-[radial-gradient(circle,var(--color-roulette-green-gradient)_0%,var(--color-roulette-green)_60%)]";
const specialNumberCls =
  "absolute top-2 left-2 text-[var(--font-display)] font-bold leading-[var(--leading-5xl)] max-[768px]:top-1 max-[768px]:left-1 max-[768px]:text-base max-[768px]:leading-4 max-[768px]:z-[5] max-[420px]:text-sm max-[420px]:leading-[var(--leading-xs)]";
const animalGridCls = "flex-1 flex flex-col gap-0.5";
const animalRowCls = "grid grid-cols-4 gap-0.5 w-full max-[768px]:gap-0.5";
const animalButtonCls =
  "w-full h-[156px] border border-[var(--color-secondary)] rounded text-white cursor-pointer flex flex-col items-center justify-center transition-all duration-200 relative p-2 overflow-hidden hover:opacity-80 hover:scale-[1.02] hover:z-10 max-[768px]:min-h-[var(--size-25)] max-[768px]:h-auto max-[768px]:py-2 max-[768px]:px-1 max-[768px]:overflow-visible max-[420px]:min-h-[var(--size-22-5)] max-[420px]:py-2 max-[420px]:px-[3px]";
const animalNumberCls =
  "absolute top-2 left-2 text-[var(--font-display)] font-bold leading-[var(--leading-5xl)] max-[768px]:top-1 max-[768px]:left-1 max-[768px]:text-base max-[768px]:leading-4 max-[768px]:z-[5] max-[420px]:text-sm max-[420px]:leading-[var(--leading-xs)]";
const animalIconCls =
  "w-[123px] h-[123px] object-contain mx-auto max-[768px]:w-[var(--size-17-5)] max-[768px]:h-[var(--size-17-5)] max-[420px]:w-[65px] max-[420px]:h-[65px]";
const redCls =
  "bg-[radial-gradient(circle,var(--color-roulette-red-light)_0%,var(--color-roulette-red)_100%)] shadow-[inset_0_0_20px_1px_rgba(0,0,0,0.6)]";
const darkCls = "bg-[radial-gradient(circle,var(--color-roulette-black-highlight)_0%,var(--color-roulette-black)_90%)]";
const bottomLeftCls = "rounded-bl-[20px] max-[768px]:rounded-bl-[16px]";
const bottomRightCls = "rounded-br-[20px] max-[768px]:rounded-br-[16px]";
const betChipCls =
  "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[var(--size-11-25)] h-[var(--size-11-25)] rounded-full bg-white border-2 border-[var(--color-roulette-red-accent)] flex items-center justify-center z-10 transition-all duration-200 shadow-[0_4px_8px_rgba(0,0,0,0.3)] pointer-events-none hover:scale-105 max-[768px]:w-[var(--size-7-5)] max-[768px]:h-[var(--size-7-5)] max-[420px]:w-[var(--size-7)] max-[420px]:h-[var(--size-7)]";
const betChipDeletionModeCls =
  "!border-[var(--color-sports-glow-to)] !bg-[var(--color-roulette-red-soft-bg)] !cursor-pointer !pointer-events-auto animate-roulette-pulse hover:!scale-[1.15] hover:!shadow-[0_0_15px_rgba(255,0,0,0.6)] [&_.betChipAmount]:!text-[var(--color-sports-glow-to)]";
const betChipAmountCls =
  "betChipAmount text-sm font-bold text-[var(--color-roulette-red-accent)] text-center max-[768px]:text-xs max-[420px]:text-[var(--font-caption)]";

const colorClsMap = {green: greenCls, red: redCls, dark: darkCls};

/**
 * NumberGrid - Renders the 40-animal betting grid
 *
 * This component displays:
 * - Special animals: 0 and 00 (top row)
 * - Main grid: 38 animals (1-38) in a 4-column layout
 *
 * The gameIdString prop allows for game-specific behavior:
 * - Different API endpoints based on the game
 * - Game-specific animal configurations from API data
 * - Custom styling or layout adjustments per game
 *
 * @param {string} gameIdString - The game identifier for game-specific logic
 * @param {Array} figures - Optional figures array from API
 */
const NumberGrid = ({
  onNumberClick,
  placedBets = [],
  maxBetPerOption = 1000,
  onMaxBetReached,
  isDeletionMode = false,
  figures = [], // New prop for API figures
}) => {
  // Get animal data from API figures or use fallback
  const getAnimalData = () => {
    if (figures && figures.length > 0) {
      const transformed = transformFiguresToAnimalData(figures);
      return transformed;
    }

    // Fallback to hardcoded data for 40 animals
    // Special animals: 0 (green), 00 (green)
    const animals = [
      {number: 0, name: "delfin", color: "green", displayName: "Delfín"},
      {number: "00", name: "ballena", color: "green", displayName: "Ballena"},
    ];

    // Generate 38 regular animals (1-38) with alternating colors
    for (let i = 1; i <= 38; i++) {
      animals.push({
        number: i,
        name: `animal${i}`,
        color: i % 2 === 1 ? "red" : "dark",
        displayName: `Animal ${i}`,
      });
    }

    return animals;
  };

  const animalData = getAnimalData();

  // Separate special numbers (0, 00) from regular numbers
  // Check for various formats: number, string, "0", 0, "00"
  const specialNumbers = animalData.filter(animal => {
    const num = animal.number;
    return num === "00" || num === 0 || num === "0" || String(num) === "00" || String(num) === "0";
  });

  // Sort special numbers in correct order: 0, 00
  specialNumbers.sort((a, b) => {
    const order = {0: 0, "00": 1};
    const aValue = order[a.number];
    const bValue = order[b.number];
    return aValue - bValue;
  });

  // Filter out special numbers - need to check all formats
  const regularNumbers = animalData.filter(animal => {
    const num = animal.number;
    return !(num === "00" || num === 0 || num === "0" || String(num) === "00" || String(num) === "0");
  });

  // Create 4xN grid layout from regular numbers (4 columns)
  const createGridLayout = () => {
    const grid = [];
    const rows = Math.ceil(regularNumbers.length / 4); // Calculate number of rows needed with 4 columns

    for (let row = 0; row < rows; row++) {
      const currentRow = [];
      for (let col = 0; col < 4; col++) {
        const index = row * 4 + col;
        if (index < regularNumbers.length) {
          currentRow.push(regularNumbers[index]);
        }
      }
      grid.push(currentRow);
    }
    return grid;
  };

  const gridLayout = createGridLayout();

  // Helper function to get fichas placed on an animal
  const getFichasForAnimal = animalId => {
    const animalBet = placedBets.find(bet => bet.animalId === animalId);
    return animalBet ? animalBet.fichas : [];
  };

  // Helper function to calculate total bet for a specific animal
  const getTotalBetForAnimal = animalId => {
    const fichas = getFichasForAnimal(animalId);
    return fichas.reduce((sum, ficha) => sum + ficha.value, 0);
  };

  // Helper function to render bet chip with sum
  const renderBetChip = animalId => {
    const fichas = getFichasForAnimal(animalId);
    if (fichas.length === 0) return null;

    const totalBet = getTotalBetForAnimal(animalId);

    return (
      <div
        className={`${betChipCls} ${isDeletionMode ? betChipDeletionModeCls : ""}`}
        title={isDeletionMode ? `Click para eliminar ($${totalBet})` : `Total apostado: ${totalBet}`}
      >
        <div className={betChipAmountCls}>{totalBet}</div>
      </div>
    );
  };

  const handleAnimalClick = animal => {
    const animalId = animal.number === "00" || animal.number === "0" ? animal.number : animal.number;

    // If in deletion mode, skip limit check and let parent handle it
    if (isDeletionMode) {
      if (onNumberClick) {
        onNumberClick({
          betType: "animal",
          value: animal.number,
          name: animal.name,
          odds: "38:1",
        });
      }
      return;
    }

    // Get the specific max bet for this animal (from API) or use default
    const animalMaxBet = animal.maxBet || maxBetPerOption;

    // Only check max bet limit when NOT in deletion mode
    const currentTotal = getTotalBetForAnimal(animalId);
    if (currentTotal >= animalMaxBet) {
      // Notify parent component about max bet reached
      if (onMaxBetReached) {
        onMaxBetReached(animalId, animalMaxBet);
      }
      return; // Don't proceed with bet
    }

    if (onNumberClick) {
      onNumberClick({
        betType: "animal",
        value: animal.number,
        name: animal.name,
        odds: "38:1",
        maxBet: animalMaxBet, // Pass the specific max bet to parent
      });
    }
  };

  // Determine last row for border radius styling
  const lastRowIndex = gridLayout.length - 1;
  const lastRow = gridLayout[lastRowIndex] || [];
  const firstAnimalInLastRow = lastRow[0]?.number;
  const lastAnimalInLastRow = lastRow[lastRow.length - 1]?.number;

  return (
    <div className={gridContainerCls}>
      {/* Top section with 2 special numbers (0, 00) - Dynamic from API */}
      <div className={topSectionCls}>
        {specialNumbers.map((animal, index) => {
          const imageUrl = animal.imageUrl || `/src/assets/images/lottery-icons/${animal.name}.svg`;
          // Apply specific classes based on position
          const specialClass = index === 0 ? special1Cls : special2Cls;

          return (
            <button
              key={animal.number}
              type="button"
              className={`${specialButtonBaseCls} ${specialClass}`}
              onClick={() => handleAnimalClick(animal)}
            >
              <span className={specialNumberCls}>{animal.number}</span>
              <img
                src={imageUrl}
                alt={animal.displayName}
                className={animalIconCls}
                loading="eager"
                onError={e => {
                  e.target.src = `/src/assets/images/lottery-icons/${animal.name}.svg`;
                }}
              />
              {renderBetChip(animal.number)}
            </button>
          );
        })}

        {/* Fallback for hardcoded special numbers if no API data */}
        {specialNumbers.length === 0 && (
          <>
            <button
              type="button"
              className={`${specialButtonBaseCls} ${special1Cls}`}
              onClick={() => handleAnimalClick({number: 0, name: "delfin", maxBet: maxBetPerOption})}
            >
              <span className={specialNumberCls}>0</span>
              <img
                src="/src/assets/images/lottery-icons/delfin.svg"
                alt="Delfín"
                className={animalIconCls}
                loading="eager"
              />
              {renderBetChip(0)}
            </button>

            <button
              type="button"
              className={`${specialButtonBaseCls} ${special2Cls}`}
              onClick={() => handleAnimalClick({number: "00", name: "ballena", maxBet: maxBetPerOption})}
            >
              <span className={specialNumberCls}>00</span>
              <img
                src="/src/assets/images/lottery-icons/ballena.svg"
                alt="Ballena"
                className={animalIconCls}
                loading="eager"
              />
              {renderBetChip("00")}
            </button>
          </>
        )}
      </div>

      {/* Main animal grid - 4 columns */}
      <div className={animalGridCls}>
        {gridLayout.map((row, rowIndex) => (
          <div
            key={`row-${row[0]?.number || `empty-${rowIndex}`}`}
            className={animalRowCls}
          >
            {row.map(animal => {
              // Determine if this is a corner cell for border radius
              const isBottomLeft = animal.number === firstAnimalInLastRow;
              const isBottomRight = animal.number === lastAnimalInLastRow;
              const cornerClass = isBottomLeft ? bottomLeftCls : isBottomRight ? bottomRightCls : "";

              return (
                <button
                  key={animal.number}
                  type="button"
                  className={`${animalButtonCls} ${colorClsMap[animal.color]} ${cornerClass}`}
                  onClick={() => handleAnimalClick(animal)}
                >
                  <span className={animalNumberCls}>{animal.number.toString().padStart(2, "0")}</span>
                  <img
                    src={animal.imageUrl || `/src/assets/images/lottery-icons/${animal.name}.svg`}
                    alt={animal.displayName || animal.name}
                    className={animalIconCls}
                    loading="eager"
                    onError={e => {
                      e.target.src = `/src/assets/images/lottery-icons/${animal.name}.svg`;
                    }}
                  />
                  {renderBetChip(animal.number)}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

NumberGrid.propTypes = {
  gameIdString: PropTypes.string.isRequired,
  onNumberClick: PropTypes.func,
  placedBets: PropTypes.arrayOf(
    PropTypes.shape({
      animalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      animalName: PropTypes.string,
      fichas: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          value: PropTypes.number,
          name: PropTypes.string,
        })
      ),
    })
  ),
  maxBetPerOption: PropTypes.number,
  onMaxBetReached: PropTypes.func,
  isDeletionMode: PropTypes.bool,
  figures: PropTypes.arrayOf(PropTypes.object),
};

export default NumberGrid;
